import { useEffect, useRef } from 'react';
import { ITiledeskAuthEvent, ITiledeskFn } from './types';

const SCRIPT_ID = 'tiledesk-jssdk';

/** Projeto em que o widget já fez login nesta página; evita logar de novo a cada refetch do token. */
let signedInProjectId: string | null = null;

export interface ITiledeskWidgetOptions {
  projectId: string;
  widgetUrl: string;
  token: string;
}

/** Mesmo stub do snippet oficial: enfileira as chamadas até o launch.js carregar e consumir a fila. */
function ensureCommandQueue(): ITiledeskFn {
  if (window.Tiledesk) return window.Tiledesk;
  const queue: ITiledeskFn = (...args: unknown[]) => queue.c?.(args);
  queue.q = [];
  queue.c = (args) => queue.q?.push(args);
  window.Tiledesk = queue;
  return queue;
}

export function openTiledeskWidget() {
  window.Tiledesk?.('open');
}

/** Encerra a sessão do visitante; SDK de terceiro, nunca deve quebrar quem chama. */
export function logoutTiledeskWidget() {
  try {
    window.Tiledesk?.('logout');
  } catch {
    /* widget não carregado ou já destruído */
  }
}

/**
 * Carrega o widget do Tiledesk do convênio e autentica o usuário com o token
 * assinado pela partiu-api. O widget é um singleton de página: uma vez
 * carregado, fica; a limpeza acontece no logout (ProfileProvider), que recarrega a página.
 */
export function useTiledeskWidget(options?: ITiledeskWidgetOptions) {
  const { projectId, widgetUrl, token } = options ?? {};
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!projectId || !widgetUrl || !tokenRef.current) return;
    if (signedInProjectId === projectId) return;

    window.tiledeskSettings = { ...window.tiledeskSettings, projectid: projectId, autoStart: false, lang: 'pt' };
    const tiledesk = ensureCommandQueue();

    tiledesk('onAuthStateChanged', (event: ITiledeskAuthEvent) => {
      if (event?.detail?.isLogged) window.Tiledesk?.('show');
    });
    tiledesk('signInWithCustomToken', tokenRef.current);
    signedInProjectId = projectId;

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = widgetUrl;
      document.body.appendChild(script);
    }
  }, [projectId, widgetUrl]);
}
